import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { CalendarIcon } from '../../components/svgs/icons/CalendarIcon';
import { Button } from '../Button';
import { Calendar } from '../Calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../Popover';
import { styleSheet } from './styles';

interface IProps {
	ariaLabel: string;
	from: string;
	to: string;
	onChange: (from: string, to: string) => void;
	/** Calendars shown side by side. Two by default; one suits a narrow filter bar. */
	numberOfMonths?: 1 | 2;
	/**
	 * Show the leading/trailing days of the neighbouring months. Off reads better across two
	 * calendars, where the same dates otherwise appear twice — greyed in one month and live in the next.
	 */
	showOutsideDays?: boolean;
	size?: 'sm';
}

const ISO_FORMAT = 'YYYY-MM-DD';
const DISPLAY_FORMAT = 'MMM D, YYYY';

const parse = (value: string): Date | undefined => {
	if (!value) {
		return undefined;
	}
	const parsed = moment(value, ISO_FORMAT, true);
	return parsed.isValid() ? parsed.toDate() : undefined;
};

const formatLabel = (from: string, to: string): string => {
	const fromDate = parse(from);
	const toDate = parse(to);
	if (fromDate && toDate) {
		return `${moment(fromDate).format(DISPLAY_FORMAT)} – ${moment(toDate).format(DISPLAY_FORMAT)}`;
	}
	if (fromDate) {
		return `From ${moment(fromDate).format(DISPLAY_FORMAT)}`;
	}
	if (toDate) {
		return `Until ${moment(toDate).format(DISPLAY_FORMAT)}`;
	}
	return 'Select date range';
};

export const DateRangePicker: React.FC<IProps> = ({
	ariaLabel,
	from,
	to,
	numberOfMonths = 2,
	onChange,
	showOutsideDays = true,
	size,
}) => {
	const [open, setOpen] = React.useState(false);
	// The first day clicked since the popover opened, until a second click closes the range.
	const [anchorDate, setAnchorDate] = React.useState<Date | null>(null);
	const fromDate = parse(from);
	const toDate = parse(to);
	const hasValue = Boolean(fromDate || toDate);

	const handleOpenChange = (nextOpen: boolean) => {
		setAnchorDate(null);
		setOpen(nextOpen);
	};

	// Two clicks always make a fresh range: the first picks one end, the second the other. React Day
	// Picker instead stretches a complete range toward each click, so a start can never move later
	// without first clearing — and a caller that falls back to a default range has nothing to clear to.
	const handleSelect = (_range: unknown, clickedDate: Date) => {
		if (!anchorDate) {
			setAnchorDate(clickedDate);
			onChange(moment(clickedDate).format(ISO_FORMAT), moment(clickedDate).format(ISO_FORMAT));
			return;
		}
		const [start, end] = clickedDate < anchorDate ? [clickedDate, anchorDate] : [anchorDate, clickedDate];
		setAnchorDate(null);
		onChange(moment(start).format(ISO_FORMAT), moment(end).format(ISO_FORMAT));
	};

	const handleClear = () => {
		setAnchorDate(null);
		onChange('', '');
	};

	return (
		<div className={css(styleSheet.triggerOuter)}>
			<Popover open={open} onOpenChange={handleOpenChange}>
				<PopoverTrigger asChild>
					<Button
						type='button'
						variant='ghost'
						styles={[
							styleSheet.trigger,
							size === 'sm' ? styleSheet.triggerSm : undefined,
							hasValue ? styleSheet.triggerHasClear : undefined,
						]}
						aria-label={ariaLabel}
					>
						<CalendarIcon />
						<span className={css(styleSheet.triggerLabel, !hasValue ? styleSheet.triggerLabelMuted : undefined)}>
							{formatLabel(from, to)}
						</span>
					</Button>
				</PopoverTrigger>
				<PopoverContent align='start' styles={[styleSheet.popoverContent]}>
					<Calendar
						mode='range'
						numberOfMonths={numberOfMonths}
						showOutsideDays={showOutsideDays}
						selected={{ from: fromDate, to: toDate }}
						defaultMonth={fromDate ?? toDate ?? new Date()}
						onSelect={handleSelect}
						captionLayout='dropdown'
					/>
					<div className={css(styleSheet.footer)}>
						<Button type='button' variant='ghost' size='sm' onClick={handleClear}>
							Clear
						</Button>
						<Button type='button' variant='primary' size='sm' onClick={() => handleOpenChange(false)}>
							Done
						</Button>
					</div>
				</PopoverContent>
			</Popover>
			{hasValue ? (
				<Button
					variant='ghost'
					size='icon'
					type='button'
					aria-label='Clear date range'
					styles={[styleSheet.clear]}
					onClick={handleClear}
				>
					×
				</Button>
			) : null}
		</div>
	);
};
